import { useDispatch, useSelector } from "react-redux";
import { modulesNameSynonymMapper } from "../../services/constants";
import { formCategories } from "@Helpers";
import { getBackendUrl } from "@Utils";
import { useEffect, useState } from "react";
import axiosWrapper from "@Services/axiosWrapper";
import { useTranslation } from "react-i18next";
import { getPortalCustomModules } from "../../actions/portalSetting.helper";
import { setProjectSideBarIconsAndNameMapper } from "../../reducers/projectSetting";

function useStickySidebar({ layout, projectId, path, isFocused = true, mobile = false }: any) {
  const { t } = useTranslation();
  const customModules = useSelector((state: any) => state.customModules) || [];
  const portalData = useSelector((state: any) => state.portalData);

  const [sideBarMidIcons, setSideBarMidIcons] = useState([]);
  const [sideBarUpperFixedIcons, setSideBarUpperFixedIcons] = useState([]);
  const [sideBarLowerFixedIcons, setSideBarLowerFixedIcons] = useState([]);
  const [counts, setCounts] = useState<any>({});

  const isRustomjeePortal = portalData?.frontendUrl?.includes("rustomjee") || false;
  const isDpmsPortal = portalData?.frontendUrl?.includes("dpmsportal") || false;

  const dispatch = useDispatch();

  useEffect(() => {
    if (isFocused && customModules) {
      setCounts({});
      if (mobile) {
        countData();
      }
    }
  }, [isFocused, customModules]);

  useEffect(() => {
    dispatch(getPortalCustomModules());
  }, []);

  async function countData() {
    [
      "INSPECTION",
      "PROGRESS_UPDATES",
      "BILLS",
      "CHANGE_ORDER",
      ...customModules.map((module: any) => {
        return module.resourceType;
      }),
    ].forEach(async (resourceType) => {
      const formLists = await axiosWrapper.post(`${getBackendUrl()}/forms/taskInspectionAndProgressFormsController`, {
        value: {
          requiresCounts: true,
          params: {
            projectId,
            resourceType,
          },
          projectId,
          resourceType,
          skip: 0,
          take: 1,
        },
      });
      setCounts((prevState: any) => ({
        ...prevState,
        [resourceType]: formLists.data.count,
      }));
    });

    const spaceList = await axiosWrapper.post(`${getBackendUrl()}/spaces/spacesData`, {
      value: {
        requiresCounts: true,
        params: {
          projectId,
        },
        projectId,
        skip: 0,
        take: 1,
      },
    });
    setCounts((prevState: any) => ({
      ...prevState,
      SPACES: spaceList.data.count,
    }));

    const contractList = await axiosWrapper.post(`${getBackendUrl()}/contracts/contractsController`, {
      value: {
        requiresCounts: true,
        where: [
          {
            isComplex: false,
            field: "parentId",
            operator: "equal",
            value: null,
            ignoreCase: false,
          },
        ],
        params: {
          projectId,
          IdMapping: "_id",
        },
        projectId,
        IdMapping: "_id",
        skip: 0,
        take: 1,
      },
    });
    setCounts((prevState: any) => ({
      ...prevState,
      CONTRACT: contractList.data.count,
    }));
  }

  useEffect(() => {
    const projectSideBarIcons: any = [
      {
        id: "Home",
        allow: path.Dashboard,
        path: path.Dashboard && path.Dashboard(projectId),
        name: t("Home"),
        svg: "HomeIcon",
        toolTip: isDpmsPortal ? `${modulesNameSynonymMapper.project} Dashboard` : t("Project Dashboard"),
        position: "upper",
      },
      {
        id: "Spaces",
        allow: path.WorkspacesList && layout.SPACES?.ENABLE,
        path: path.WorkspacesList && path.WorkspacesList(projectId),
        count: counts.SPACES,
        // TODO: add additional properitey type because in dpmsportal, it is not highlighting
        name: isDpmsPortal ? `${modulesNameSynonymMapper.spaces}` : t("Spaces"),
        svg: "SpacesIcon",
        toolTip: isDpmsPortal ? `${modulesNameSynonymMapper.spaces}` : t("Spaces"),
        position: "upper",
      },
      {
        id: "Transmittals",
        allow: path.Transmittals && layout.TRANSMITTAL?.ENABLE,
        path: path.Transmittals && path.Transmittals(projectId),
        name: t("Transmittals"),
        svg: "TransmittalIcon",
        toolTip: t("Transmittals"),
        position: "upper",
      },
      {
        id: "Mailbox",
        allow: path.Mailbox && layout.MAILBOX?.ENABLE,
        path: path.Mailbox && path.Mailbox(projectId),
        name: t("Mailbox"),
        svg: "Mailbox",
        toolTip: t("Mailbox"),
        position: "upper",
      },
      {
        id: "Files",
        allow: path.Files && layout.FILES?.ENABLE,
        path: path.Files && path.Files(projectId),
        name: t("Files"),
        svg: "FilesIcon",
        toolTip: t("Files"),
        position: "mid",
      },
      {
        id: "Issues",
        allow: path.Issues && layout.ISSUES?.ENABLE,
        path: path.Issues && path.Issues(projectId),
        name: t("Issues"),
        svg: "IssuesIcon",
        toolTip: t("Issues"),
        position: "mid",
      },
      {
        id: "Tasks",
        allow: path.Tasks && layout.TASKS?.ENABLE,
        path: path.Tasks && path.Tasks(projectId),
        name: t("Tasks"),
        svg: "TasksIcon",
        toolTip: t("Tasks"),
        position: "mid",
      },
      // {
      //   id:"Change Order",
      //   allow: layout.FILES?.ENABLE,
      //   path: '/changeOrderList',
      //   name: 'Change Order',
      //   svg: 'MdOutlineChangeCircle',
      //   toolTip: 'Change Order',
      // position : "mid"
      // },
      {
        id: "Estimates",
        allow: path.Estimates && layout.ESTIMATES?.ENABLE,
        path: path.Estimates && path.Estimates(projectId),
        name: t("Estimates"),
        svg: "EstimateIcon",
        toolTip: t("Estimates"),
        position: "mid",
      },
      {
        id: "Schedules",
        allow: path.Schedules && layout.SCHEDULES?.ENABLE,
        path: path.Schedules && path.Schedules(projectId),
        name: t("Schedules"),
        svg: "ScheduleIcon",
        toolTip: t("Schedules"),
        position: "mid",
      },
      {
        id: "Contracts",
        allow: path.Contract && layout.CONTRACT?.ENABLE,
        count: counts.CONTRACT,
        historyPush: false,
        path: path.Contract && path.Contract(projectId),
        name: t("Contracts"),
        svg: "Contracts",
        toolTip: t("Contracts"),
        position: "mid",
      },
      {
        id: "Assets",
        allow: path.Assets && layout.ASSET_REGISTER?.ENABLE,
        path: path.Assets && path.Assets(projectId),
        name: t("Assets"),
        svg: "Assets",
        toolTip: t("Assets"),
        position: "mid",
      },
      {
        id: "Reviews",
        allow: path.Reviews && layout.WORKFLOW?.ENABLE,
        path: path.Reviews && path.Reviews(projectId),
        name: t("Reviews"),
        svg: "Review",
        toolTip: t("Reviews"),
        position: "mid",
      },
      {
        id: "Inspections",
        allow: path.Forms && layout.INSPECTION?.ENABLE,
        count: counts.INSPECTION,
        path: path.Forms && path.Forms(projectId, formCategories.inspection),
        name: isRustomjeePortal ? "Work Done" : t("Inspections"),
        svg: "inspectionForm",
        toolTip: `${isRustomjeePortal ? "Work Done Forms" : t("Inspection Forms")}`,
        position: "mid",
      },
      {
        id: "Progress Updates",
        allow: path.Forms && layout.PROGRESS_UPDATES?.ENABLE,
        count: counts.PROGRESS_UPDATES,
        path: path.Forms && path.Forms(projectId, formCategories.progressUpdates),
        name: t("Progress Updates"),
        svg: "ProgressUpdates",
        toolTip: t("Progress Updates"),
        position: "mid",
      },
      {
        id: "Bills",
        allow: path.Forms && layout.BILLS?.ENABLE,
        count: counts.BILLS,
        path: path.Forms && path.Forms(projectId, formCategories.bills),
        name: t("Bills"),
        svg: "bills",
        toolTip: t("Bills"),
        position: "mid",
      },
      {
        id: "Change Orders",
        allow: path.Forms && layout.CHANGE_ORDER?.ENABLE,
        count: counts.CHANGE_ORDER,
        path: path.Forms && path.Forms(projectId, formCategories.changeOrder),
        name: t("Change Orders"),
        svg: "changeOrder",
        toolTip: t("Change Orders"),
        position: "mid",
      },
      {
        id: "Payments",
        allow: path.Forms && layout.PAYMENTS?.ENABLE,
        count: counts.PAYMENTS,
        path: path.Forms && path.Forms(projectId, formCategories.payments),
        name: t("Payments"),
        svg: "Payments",
        toolTip: t("Payments"),
        position: "mid",
      },
      {
        id: "Risk Management",
        allow: path.RiskManagement && layout.RISK_MANAGEMENT?.ENABLE,
        path: path.RiskManagement && path.RiskManagement(projectId),
        name: t("Risk Management"),
        svg: "RiskManagementIcon",
        toolTip: t("Risk Management"),
        position: "mid",
      },
      // {
      //   id:"Tenders",
      //   allow: layout.TENDER_LIST?.ENABLE,
      //   path: '/tendersList',
      //   name: 'Tenders',
      //   svg: 'TendersIcon',
      //   toolTip: 'Tenders',
      // position : "mid"
      // },
      // {
      //   id: "Asset Register",
      //   allow: layout.ASSET_REGISTER?.ENABLE,
      //   path: '/assetRegister',
      //   name: 'Asset Register',
      //   svg: 'AssetRegisterIcon',
      //   toolTip: 'Asset Register',
      // position : "mid"
      // },
      // {
      //   id:"Procurement",
      //   allow: layout.PROCUREMENT?.ENABLE,
      //   path: '/procurement',
      //   name: 'Procurement',
      //   svg: 'ProcurementIcon',
      //   toolTip: 'Procurement',
      // position : "mid"
      // },
      // {
      //   id: "Workflows",
      //   allow: layout.WORKFLOW?.ENABLE,
      //   path: '/workflow',
      //   name: 'Workflows',
      //   svg: 'TiFlowChildren',
      //   toolTip: 'Workflows',
      // position : "mid"
      // },
      // {
      //   id: "ChangeOrder",
      //   allow: layout.CHANGE_ORDER?.ENABLE,
      //   path: '/changeOrder',
      //   name: 'ChangeOrder',
      //   svg: 'RiExchangeBoxLine',
      //   toolTip: 'Change Order',
      // position : "mid"
      // },
      {
        id: "Clash",
        showBetaTag: true,
        allow: path.GeneralClash && layout.CLASH?.ENABLE,
        path: path.GeneralClash && path.GeneralClash(projectId),
        name: t("Clash"),
        svg: "ClashIcon",
        toolTip: t("Clash"),
        position: "mid",
      },
      {
        id: "Settings",
        allow: true,
        path: path.Settings && path.Settings(projectId),
        name: t("Settings"),
        svg: "SettingsIcon",
        toolTip: isDpmsPortal ? `${modulesNameSynonymMapper.project} Settings` : t("Project Settings"),
        position: "bottom",
      },
    ];

    customModules.map((module: any) => {
      projectSideBarIcons.push({
        id: module.name,
        allow: path.Forms,
        path: path.Forms && path.Forms(projectId, module.resourceType),
        count: counts[module.resourceType],
        name: module.name,
        svg: module.icon,
        toolTip: module.name,
        position: "mid",
      });
    });
    dispatch(
      setProjectSideBarIconsAndNameMapper(
        projectSideBarIcons.map(({ id, name, svg, toolTip }: any) => {
          return { id, name, svg, toolTip };
        }),
      ),
    );
    setSideBarMidIcons(projectSideBarIcons.filter((icon: any) => icon.position == "mid"));
    setSideBarUpperFixedIcons(projectSideBarIcons.filter((icon: any) => icon.position == "upper"));
    setSideBarLowerFixedIcons(projectSideBarIcons.filter((icon: any) => icon.position == "bottom"));
  }, [layout, projectId, customModules, counts, portalData]);

  return { sideBarMidIcons, sideBarUpperFixedIcons, sideBarLowerFixedIcons };
}

export default useStickySidebar;
